import { showErrorResponseNotification } from "@/utils/apiUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { Notification } from "@/utils/notificationUtils";
import { dateToIsoDateString, numberOrZero } from "@/utils/utils";
import { FormInstance } from "antd/lib/form";
import dayjs from "dayjs";
import t, { DEFAULT_LOCALE } from "../../../app/i18n";
import { HttpStatus } from "../../../common/api/http";
import { ErrorResponse, FieldConstraintViolation } from "../../../common/types";
import type { DeepPartial, UUID } from "../../../typings/global";
import { ClientFormType, ClientType } from "../../client/enums";
import { Client, LegalClient, NaturalClient, SelfEmployedClient } from "../../client/types";
import {
  getCreateUpdateContractClientFromClient,
  isLegalClient,
  processClientsDataViolations,
} from "../../client/utils";
import { PaymentFrequency } from "../../contract/enums";
import {
  Contract,
  CrashInsuranceData,
  GapInsuranceData,
  MtplInsuranceData,
  RealtyInsurance,
  StandardVehicleInsurance,
  TravelInsurance,
} from "../../contract/types";
import {
  convertInsuranceTypeToCalcType,
  isInsuranceContract,
  isRealtyInsurance,
  isTravelInsurance,
  isTravelOrRealtyOrVehicleInsurance,
  isVehicleInsurance,
} from "../../contract/utils";
import { CalcDraft, RealtyCalcDraft, TravelCalcDraft, VehicleCalcDraft } from "../drafts/types";
import { CalcType, ClientExperience } from "../enums";
import { CalcResultDataType, CalcResultsSorting } from "./enums";
import { RealtyCalc, RealtyCalcResultData, RealtyGen } from "./realty/types";
import { TravelCalc, TravelCalcResultData, TravelGen } from "./travel/types";
import { CalcResult, CalcResultData, CalculatorsGen, GenFinancialMediationData, Offer, StepInfo } from "./types";
import {
  AnimalReinsuranceAmount,
  VehicleOwnerRelation,
  VehiclePolicyHolderRelation,
  VehicleSecurityFeature,
} from "./vehicle/enums";
import { VehicleCalc, VehicleCalcResultData, VehicleCalcType, VehicleGen } from "./vehicle/types";

export const DEFEND_D2C_COVERAGE = "D2C";

const institutionNameCollator = new Intl.Collator(DEFAULT_LOCALE, { sensitivity: "accent" });

export const sortAndGroupCalcResults = <U extends CalcType, T extends CalcResultData = CalcResultData>(
  calcResults: CalcResult<T, U>[],
): CalcResult<T, U>[][] => {
  const groupsByInstitutionId = new Map<UUID, CalcResult<T, U>[]>();

  calcResults.forEach((result) => {
    const group = groupsByInstitutionId.get(result.insuranceInstitution.id);

    if (group) {
      group.push(result);
    } else {
      groupsByInstitutionId.set(result.insuranceInstitution.id, [result]);
    }
  });

  const rowsWithoutError: CalcResult<T, U>[][] = [];
  const rowsWithError: CalcResult<T, U>[][] = [];

  groupsByInstitutionId.forEach((resultsRow) => {
    if (resultsRow.some((result) => result.error)) {
      rowsWithError.push(resultsRow);
    } else {
      rowsWithoutError.push(resultsRow);
    }
  });

  return sortResultsWithinGroupsByCoverageIndex([
    ...sortResultGroupsByInstitutionName(rowsWithoutError),
    ...sortResultGroupsByInstitutionName(rowsWithError),
  ]);
};

export const checkIfStepsAreSuccessful = <T>(steps: StepInfo<T>[], ...checkedSteps: T[]): boolean => {
  return checkedSteps.every((checkedStep) => steps.some((step) => step.step === checkedStep && step.successful));
};

export const isApplicableResult = <T extends CalcResultData = CalcResultData>(result: CalcResult<T>): boolean => {
  return !result.data?.notApplicable;
};

export const filterApplicableSuccessResults = <T extends CalcResultData = CalcResultData>(
  results: CalcResult<T>[],
): (CalcResult<T> & { data: T })[] => {
  return results.filter(isApplicableSuccessResult);
};

export const isApplicableSuccessResult = <T extends CalcResultData = CalcResultData>(
  result: CalcResult<T>,
): result is CalcResult<T> & { data: T } => {
  return !!result.data && !result.data.notApplicable;
};

export const sortResultGroupsByInstitutionName = <T extends CalcResultData, U extends CalcType>(
  resultGroups: CalcResult<T, U>[][],
): CalcResult<T, U>[][] => {
  return [...resultGroups].sort((a, b) => {
    const institutionNameA = a[0]?.insuranceInstitution.name ?? "";
    const institutionNameB = b[0]?.insuranceInstitution.name ?? "";

    return institutionNameCollator.compare(institutionNameA, institutionNameB);
  });
};

export const groupByInstitution = <R extends CalcResult>(results: R[]): R[][] => {
  const grouped = results.reduce(
    (acc, result) => {
      const institutionId = result.insuranceInstitution.id;
      if (!acc[institutionId]) {
        acc[institutionId] = [];
      }
      acc[institutionId]?.push(result);
      return acc;
    },
    {} as Record<string, R[]>,
  );

  return Object.values(grouped);
};

export const compareByCoverageOrderIndex = <T extends CalcResultData>(a: CalcResult<T>, b: CalcResult<T>): number => {
  return (a.coverageOrderIndex ?? Number.POSITIVE_INFINITY) - (b.coverageOrderIndex ?? Number.POSITIVE_INFINITY);
};

export const sortResultsWithinGroupsByCoverageIndex = <T extends CalcResultData, U extends CalcType>(
  resultGroups: CalcResult<T, U>[][],
): CalcResult<T, U>[][] => {
  return resultGroups.map((group) => [...group].sort(compareByCoverageOrderIndex));
};

interface GenResponseViolations {
  clientsViolations: Map<ClientFormType, FieldConstraintViolation[]>;
  notificationViolations: FieldConstraintViolation[];
}

export const processGenResultError = (
  error: ErrorResponse,
  clientsIndicesMap: Map<ClientFormType, number>,
  form: FormInstance,
  translationRootPath: string,
): GenResponseViolations | undefined => {
  if (error.status === HttpStatus.UNPROCESSABLE_ENTITY && error.violations) {
    const clientsViolations = processClientsDataViolations(clientsIndicesMap, "clientsData.clients", error.violations);
    const notificationViolations = error.violations.filter(
      (violation) => !violation.fieldPath.startsWith("clientsData.clients"),
    );

    if (clientsViolations.size > 0) {
      notificationViolations.unshift({
        fieldPath: "clientsData._label",
        errors: [t("calc.validations.clientsViolationError")],
        fieldValue: undefined,
      });
    }

    setErrorsToForm(form, translationRootPath, error.violations);

    return { clientsViolations, notificationViolations };
  }

  showErrorResponseNotification(error);
  return undefined;
};

export const resolveClientFormTypeIdentifierName = (type: ClientFormType): string => {
  switch (type) {
    case ClientFormType.INSURED:
      return "insuredClientIdentifier";
    case ClientFormType.POLICY_HOLDER:
      return "policyHolderIdentifier";
    case ClientFormType.REPRESENTATIVE:
      return "representativeIdentifier";
    case ClientFormType.VINCULATION:
      return "vinculationClientIdentifier";
    case ClientFormType.HOLDER:
      return "holderIdentifier";
    case ClientFormType.OWNER:
      return "ownerIdentifier";
    case ClientFormType.CO_DEBTOR:
      return "coDebtorIdentifier";
    default:
      return "";
  }
};

const getDefaultGenFinancialMediationData = (policyHolderType: ClientType): Partial<GenFinancialMediationData> => ({
  clientExperience:
    policyHolderType === ClientType.SELF_EMPLOYED || policyHolderType === ClientType.LEGAL
      ? ClientExperience.SUFFICIENT
      : undefined,
  otherClientRequirements: undefined,
  recommendedResult: undefined,
  recommendationReason: undefined,
  additionalClientStatement: undefined,
  additionalSuitabilityStatement: undefined,
});

const prepareClientsData = (clients: (NaturalClient | SelfEmployedClient | LegalClient)[]) => {
  return clients.map((client) => {
    const clientData = getCreateUpdateContractClientFromClient(client);

    if (isLegalClient(client)) {
      return { ...clientData };
    }

    return {
      ...clientData,
      client: {
        ...clientData.client,
        identityCardNumber: undefined,
        previousIdentityCardNumber: client.identityCardNumber,
      },
    };
  });
};

type PrepareGenDataFromContract = {
  insurance: TravelInsurance | RealtyInsurance | StandardVehicleInsurance;
  contractData: {
    policyHolderIndex: number;
    clients: Client[];
    paymentFrequency: PaymentFrequency;
  };
};

export const prepareCalcGenDataFromContract = ({
  insurance,
  contractData,
}: PrepareGenDataFromContract): CalculatorsGen | undefined => {
  try {
    const cloneCalcData = structuredClone(insurance.calcData);

    if (!cloneCalcData) {
      return prepareCalcGenDataFromInsurance({ insurance, contractData });
    }

    return prepareCalcGenDataFromInsuranceCalcData({
      insurance,
      contractData,
      calcData: cloneCalcData,
    });
  } catch {
    Notification.getNotification().error({
      title: t("common.error"),
      description: t("calc.helpers.loadDataError"),
      key: "loadCalcDataError",
    });

    return undefined;
  }
};

type PrepareGenDataFromInsurance = {
  insurance: TravelInsurance | RealtyInsurance | StandardVehicleInsurance;
  contractData: {
    policyHolderIndex: number;
    clients: Client[];
    paymentFrequency: PaymentFrequency;
  };
};

const prepareCalcGenDataFromInsurance = ({
  insurance,
  contractData: { policyHolderIndex, clients, paymentFrequency },
}: PrepareGenDataFromInsurance): CalculatorsGen | undefined => {
  if (isTravelInsurance(insurance)) {
    return {
      generalData: {
        effectiveBeginningDate: dateToIsoDateString(dayjs()),
        effectiveEndDate: undefined,
        territorialValidity: insurance.insuranceData.territorialValidity,
        riskGroup: insurance.insuranceData.riskGroup,
        studyStay: false,
      },
      clientsData: {
        policyHolderIndex: policyHolderIndex,
        clients: [...prepareClientsData(clients)],
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
      },
      type: CalcType.TRAVEL,
    } as DeepPartial<TravelGen>;
  } else if (isRealtyInsurance(insurance)) {
    return {
      type: CalcType.REALTY,
      generalBuildingData: {
        type: insurance.insuranceData.buildingType,
        placeOfInsurance: insurance.insuranceData.placeOfInsurance,
        state: insurance.insuranceData.buildingState,
        constructionYear: insurance.insuranceData.constructionYear,
        permanentlyOccupied: insurance.insuranceData.permanentlyOccupied,
        motionSensor: false,
        fireSensor: false,
        waterSensor: false,
      },
      houseOrRecreationalBuildingData: {
        buildArea: insurance.insuranceData.houseOrRecreationalBuilding?.buildArea,
        roofType: insurance.insuranceData.houseOrRecreationalBuilding?.roofType,
        constructionMaterial: insurance.insuranceData.houseOrRecreationalBuilding?.constructionMaterial,
      },
      apartmentData: {
        floor: insurance.insuranceData.apartment?.floor,
        floorArea: insurance.insuranceData.apartment?.floorArea,
      },
      realtyInsuranceData: insurance.insuranceData.realty?.insuranceAmount
        ? {
            insuranceAmount: insurance.insuranceData.realty?.insuranceAmount,
            nearbyBuildingReinsurances: [],
          }
        : undefined,
      householdInsuranceData: insurance.insuranceData.household?.insuranceAmount
        ? {
            insuranceAmount: insurance.insuranceData.household?.insuranceAmount,
            equipmentReinsurances: [],
          }
        : undefined,
      generalInsuranceData: {
        effectiveBeginningDate: dateToIsoDateString(dayjs().add(1, "day")),
        paymentFrequency: paymentFrequency,
        crossSelling: {
          csobContract: false,
          generaliContract: false,
          unionHealthContract: false,
          uniqaContractNumber: undefined,
          premiumContractNumber: undefined,
          colonnadeDiscountCode: undefined,
        },
        cyberneticReinsurance: insurance.insuranceData.reinsurances.cyberSecurity,
        warrantyReinsurance: insurance.insuranceData.reinsurances.warranty,
        cyclingReinsurance: insurance.insuranceData.reinsurances.cycling,
        loanReinsurance: insurance.insuranceData.reinsurances.loan,
        rentReinsurance: insurance.insuranceData.reinsurances.rent,
        allRiskReinsurance: insurance.insuranceData.reinsurances.allRisk,
        complicity: insurance.insuranceData.complicity,
        vinculation: insurance.insuranceData.vinculation,
      },
      clientsData: {
        policyHolderIndex: policyHolderIndex,
        insuredClientIndex: insurance.insuredClientIndex,
        vinculationClientIndex: insurance.vinculationClientIndex,
        clients: [...prepareClientsData(clients)],
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
        ownedByClient: true,
        businessDesc: undefined,
      },
      liabilityInsuranceData: {
        liability: undefined,
      },
    } as DeepPartial<RealtyGen>;
  } else if (isVehicleInsurance(insurance)) {
    const calcType = convertInsuranceTypeToCalcType(insurance);
    const clientsDataClients = [...prepareClientsData(clients)];
    const isNotNaturalClient = clientsDataClients[insurance.vehicleHolderIndex]?.client.type !== ClientType.NATURAL;
    const ownerRelation = insurance.insuranceData.leasing
      ? VehicleOwnerRelation.DIFFERENT_FROM_BOTH
      : isNotNaturalClient
        ? VehicleOwnerRelation.SAME_AS_VEHICLE_HOLDER
        : undefined;

    return {
      type: calcType,
      vehicleData: {
        licensePlate: insurance.licensePlate,
        vin: insurance.vehicle.vin,
        purpose: insurance.insuranceData.purpose,
        registrationCertificateNumber: undefined,
        previousRegistrationCertificateNumber: insurance.insuranceData.registrationCertificateNumber,
        firstRegistrationDate: insurance.vehicle.firstRegistrationDate,
        modelId: insurance.vehicle.model.id,
        brandId: insurance.vehicle.model.brand.id,
        customModel: insurance.vehicle.customModel,
        customBrand: insurance.vehicle.customBrand,
        category: insurance.vehicle.category,
        engineDisplacement: insurance.vehicle.engineDisplacement,
        enginePower: insurance.vehicle.enginePower,
        electricEnginePower: insurance.vehicle.electricEnginePower,
        fuelType: insurance.vehicle.fuelType,
        transmission: insurance.vehicle.transmission,
        bodywork: insurance.vehicle.bodywork,
        seatsNumber: insurance.vehicle.seatsNumber,
        doorsNumber: insurance.vehicle.doorsNumber,
        fourWheelDrive: insurance.vehicle.fourWheelDrive,
        totalWeight: insurance.vehicle.totalWeight,
        colorId: insurance.vehicle.color.id,
        price: undefined,
        generalPrice: undefined,
        buyingPrice: undefined,
        odometer: undefined,
        odometerPerYear: undefined,
        newVehicle: false,
        security: Object.values(VehicleSecurityFeature).map((feature) => ({ feature: feature, selected: false })),
      },
      clientsData: {
        policyHolderIndex: policyHolderIndex,
        holderIndex: insurance.vehicleHolderIndex,
        ownerIndex: insurance.vehicleOwnerIndex,
        clients: clientsDataClients,
        holderTimeWithoutAccident: undefined,
        holderAccidentsIn2Years: undefined,
        holderAccidentsIn3Years: undefined,
        holderAccidentsIn5Years: undefined,
        holderAccidentsIn8Years: undefined,
        holderAccidentsIn10Years: undefined,
        holderAccidentsInAllYears: undefined,
        currentlyInsuredVehicles: undefined,
        leasing: insurance.insuranceData.leasing,
        policyHolderRelation:
          isNotNaturalClient || insurance.insuranceData.leasing
            ? VehiclePolicyHolderRelation.SAME_AS_VEHICLE_HOLDER
            : undefined,
        ownerRelation: ownerRelation,
      },
      generalData: {
        effectiveBeginningDate: dateToIsoDateString(dayjs()),
        mtplInsuranceEffectiveBeginningDate: undefined,
        crossSelling: {
          kooperativaContract: false,
          unionHealthContract: false,
          uniqaContract: false,
          pillowDiscountCode: undefined,
          uniqaContractNumber: undefined,
        },
        gapDuration: (insurance.insuranceData as GapInsuranceData).duration,
        paymentFrequency: paymentFrequency,
      },
      reinsurancesData: {
        extendedAssistance: (insurance.insuranceData as MtplInsuranceData | CrashInsuranceData).coverages
          .extendedAssistance,
        glass: (insurance.insuranceData as MtplInsuranceData | CrashInsuranceData).coverages.glass,
        animal: calcType === CalcType.MTPL ? (insurance.insuranceData as MtplInsuranceData).coverages.animal : false,
        element: calcType === CalcType.MTPL ? (insurance.insuranceData as MtplInsuranceData).coverages.element : false,
        theft: calcType === CalcType.MTPL ? (insurance.insuranceData as MtplInsuranceData).coverages.theft : false,
        vandalism:
          calcType === CalcType.MTPL ? (insurance.insuranceData as MtplInsuranceData).coverages.vandalism : false,
        injury: calcType === CalcType.CRASH ? false : (insurance.insuranceData as MtplInsuranceData).coverages.injury,
        gap: calcType === CalcType.MTPL ? false : (insurance.insuranceData as CrashInsuranceData).coverages.gap,
        gapComplicityReinsurance: (insurance.insuranceData as CrashInsuranceData).coverages.gapComplicityReinsurance,
        gapDuration: (insurance.insuranceData as CrashInsuranceData).coverages.gapDuration,
        replacementVehicle:
          calcType === CalcType.MTPL
            ? false
            : (insurance.insuranceData as CrashInsuranceData).coverages.replacementVehicle,
        generaliAbroadVehicleRepair:
          calcType === CalcType.CRASH
            ? (insurance.insuranceData as CrashInsuranceData).coverages.abroadVehicleRepair
            : false,
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
        recommendedGapComplicityReinsurance: false,
        recommendedGapCoverageLimit: undefined,
      },
    } as DeepPartial<VehicleGen>;
  }

  return;
};

type PrepareCalcGenDataFromContractCalcData = {
  contractData: {
    policyHolderIndex: number;
    clients: Client[];
    paymentFrequency: PaymentFrequency;
  };
  calcData: CalculatorsGen;
  insurance: TravelInsurance | RealtyInsurance | StandardVehicleInsurance;
};

const prepareCalcGenDataFromInsuranceCalcData = ({
  contractData: { policyHolderIndex, clients },
  calcData,
  insurance,
}: PrepareCalcGenDataFromContractCalcData): CalculatorsGen | undefined => {
  if (isVehicleGenData(calcData)) {
    return {
      ...calcData,
      generalData: {
        ...calcData.generalData,
        effectiveBeginningDate: dateToIsoDateString(dayjs()),
        mtplInsuranceEffectiveBeginningDate: undefined,
        crossSelling: {
          kooperativaContract: false,
          unionHealthContract: false,
          uniqaContract: false,
          uniqaContractNumber: undefined,
          pillowDiscountCode: undefined,
        },
      },
      clientsData: {
        ...calcData.clientsData,
        policyHolderIndex: policyHolderIndex,
        holderIndex: (insurance as StandardVehicleInsurance).vehicleHolderIndex,
        ownerIndex: (insurance as StandardVehicleInsurance).vehicleOwnerIndex,
        clients: [...prepareClientsData(clients)],
        holderTimeWithoutAccident: undefined,
        holderAccidentsIn2Years: undefined,
        holderAccidentsIn3Years: undefined,
        holderAccidentsIn5Years: undefined,
        holderAccidentsIn8Years: undefined,
        holderAccidentsIn10Years: undefined,
        holderAccidentsInAllYears: undefined,
        currentlyInsuredVehicles: undefined,
      },
      vehicleData: {
        ...calcData.vehicleData,
        licensePlate: calcData.vehicleData.licensePlate ?? (insurance as StandardVehicleInsurance).licensePlate,
        price: undefined,
        generalPrice: undefined,
        buyingPrice: undefined,
        odometer: undefined,
        odometerPerYear: undefined,
        newVehicle: false,
        registrationCertificateNumber: undefined,
        previousRegistrationCertificateNumber: calcData.vehicleData.registrationCertificateNumber,
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
        recommendedGapComplicityReinsurance: false,
        recommendedGapCoverageLimit: undefined,
      },
      reinsurancesData: {
        ...calcData.reinsurancesData,
        animalAmount:
          calcData.reinsurancesData.animalAmount &&
          Object.values(AnimalReinsuranceAmount).includes(calcData.reinsurancesData.animalAmount)
            ? calcData.reinsurancesData.animalAmount
            : undefined,
      },
    } as DeepPartial<VehicleGen>;
  } else if (isRealtyGenData(calcData)) {
    return {
      ...calcData,
      generalInsuranceData: {
        ...calcData.generalInsuranceData,
        effectiveBeginningDate: dateToIsoDateString(dayjs().add(1, "day")),
        crossSelling: {
          csobContract: false,
          generaliContract: false,
          unionHealthContract: false,
          uniqaContractNumber: undefined,
          premiumContractNumber: undefined,
          colonnadeDiscountCode: undefined,
        },
      },
      clientsData: {
        ...calcData.clientsData,
        policyHolderIndex: policyHolderIndex,
        insuredClientIndex: (insurance as RealtyInsurance).insuredClientIndex,
        vinculationClientIndex: (insurance as RealtyInsurance).vinculationClientIndex,
        clients: [...prepareClientsData(clients)],
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
        ownedByClient: true,
        businessDesc: undefined,
      },
    } as DeepPartial<RealtyGen>;
  } else if (isTravelGenData(calcData)) {
    return {
      ...calcData,
      generalData: {
        ...calcData.generalData,
        effectiveBeginningDate: dateToIsoDateString(dayjs()),
        effectiveEndDate: undefined,
        studyStay: false,
      },
      clientsData: {
        ...calcData.clientsData,
        policyHolderIndex: policyHolderIndex,
        clients: [...prepareClientsData(clients)],
      },
      cancellationData: {
        ...calcData.cancellationData,
        tripPrice: undefined,
        firstPaymentDate: undefined,
      },
      financialMediationData: {
        ...getDefaultGenFinancialMediationData(clients[policyHolderIndex]?.type as ClientType),
      },
      coveragesData: {
        ...calcData.coveragesData,
        cancellationData: {
          ...calcData.coveragesData?.cancellationData,
          tripPrice: undefined,
          firstPaymentDate: undefined,
        },
      },
    } as DeepPartial<TravelGen>;
  }

  return;
};

export const createCalcGenDataFromContract = (contract: Contract): CalculatorsGen | undefined => {
  if (!isInsuranceContract(contract)) {
    return;
  }

  const insurance = contract.insurances[0];

  if (!insurance || !isTravelOrRealtyOrVehicleInsurance(insurance)) {
    return;
  }

  return prepareCalcGenDataFromContract({
    contractData: {
      policyHolderIndex: contract.policyHolderIndex,
      clients: contract.clients,
      paymentFrequency: contract.paymentFrequency,
    },
    insurance,
  });
};

export type SortCalcResultItemsParams<T extends CalcResultData> = {
  sortBy: CalcResultsSorting;
  items: CalcResult<T>[];
};

export const sortCalcResultItems = <T extends CalcResultData>({ sortBy, items }: SortCalcResultItemsParams<T>) => {
  const itemsWithErrorKeys = new Set(items.filter((item) => item.error).map((item) => item.id));

  return [...items].sort((itemA, itemB) => {
    const priorityA = itemsWithErrorKeys.has(itemA.id) ? 2 : !itemA.active ? 1 : 0;
    const priorityB = itemsWithErrorKeys.has(itemB.id) ? 2 : !itemB.active ? 1 : 0;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    switch (sortBy) {
      case CalcResultsSorting.INSITUTION:
        return itemA.insuranceInstitution.name.localeCompare(itemB.insuranceInstitution.name);
      case CalcResultsSorting.PRICE_ASC:
        return numberOrZero(itemA.data?.annualPremium) - numberOrZero(itemB.data?.annualPremium);
      case CalcResultsSorting.PRICE_DESC:
        return numberOrZero(itemB.data?.annualPremium) - numberOrZero(itemA.data?.annualPremium);
      case CalcResultsSorting.RECOMMENDED:
        return compareByCoverageOrderIndex(itemA, itemB);
      default:
        return 0;
    }
  });
};

/**
 * Lists set from sortCalcResultItems() already have the active items first, so only toggling an item's activation
 * needs to restore that order. Items with an error keep their positions.
 */
export const moveActiveItemsFirst = <T extends CalcResultData>(items: CalcResult<T>[]): CalcResult<T>[] => {
  const itemsWithoutError = items
    .filter((item) => !item.error)
    .sort((a, b) => {
      const isIncludedA = a.active ? 1 : 0;
      const isIncludedB = b.active ? 1 : 0;

      return isIncludedB - isIncludedA;
    });

  return items.map((item) => (item.error ? item : itemsWithoutError.shift()!));
};

export const isRealtyGenData = (calcData: CalculatorsGen): calcData is RealtyGen => {
  return calcData.type === CalcType.REALTY;
};

export const isTravelGenData = (calcData: CalculatorsGen): calcData is TravelGen => {
  return calcData.type === CalcType.TRAVEL;
};

export const isVehicleGenData = (calcData: CalculatorsGen): calcData is VehicleGen => {
  return calcData.type ? isVehicleCalcType(calcData.type) : false;
};

export const isVehicleCalcType = (calcType: CalcType): calcType is VehicleCalcType =>
  calcType === CalcType.MTPL ||
  calcType === CalcType.MTPL_CRASH ||
  calcType === CalcType.GAP ||
  calcType === CalcType.CRASH ||
  calcType === CalcType.PAS;

export const isRealtyCalcDraft = (calcDraft: CalcDraft): calcDraft is RealtyCalcDraft =>
  calcDraft.calcType === CalcType.REALTY;

export const isTravelCalcDraft = (calcDraft: CalcDraft): calcDraft is TravelCalcDraft =>
  calcDraft.calcType === CalcType.TRAVEL;

export const isVehicleCalcDraft = (calcDraft: CalcDraft): calcDraft is VehicleCalcDraft =>
  isVehicleCalcType(calcDraft.calcType);

export const isTravelCalcData = (calcData: TravelCalc | RealtyCalc | VehicleCalc): calcData is TravelCalc =>
  calcData.type === CalcType.TRAVEL;

export const isRealtyCalcData = (calcData: TravelCalc | RealtyCalc | VehicleCalc): calcData is RealtyCalc =>
  calcData.type === CalcType.REALTY;

export const isVehicleCalcData = (calcData: TravelCalc | RealtyCalc | VehicleCalc): calcData is VehicleGen =>
  isVehicleCalcType(calcData.type);

export const isTravelOffer = (
  offer: Offer<TravelCalc | RealtyCalc | VehicleCalc>,
): offer is Offer<TravelCalc, TravelCalcResultData> => offer.calcRequest.type === CalcType.TRAVEL;

export const isRealtyOffer = (
  offer: Offer<TravelCalc | RealtyCalc | VehicleCalc>,
): offer is Offer<RealtyCalc, RealtyCalcResultData> => offer.calcRequest.type === CalcType.REALTY;

export const isVehicleOffer = (
  offer: Offer<TravelCalc | RealtyCalc | VehicleCalc>,
): offer is Offer<VehicleCalc, VehicleCalcResultData> => isVehicleCalcType(offer.calcRequest.type);

export const isRealtyCalcResultsData = (
  data: TravelCalcResultData | RealtyCalcResultData | VehicleCalcResultData,
): data is RealtyCalcResultData =>
  data.type === CalcResultDataType.REALTY || data.type === CalcResultDataType.REALTY_ALLIANZ;

export const isTravelCalcResultsData = (
  data: TravelCalcResultData | RealtyCalcResultData | VehicleCalcResultData,
): data is TravelCalcResultData =>
  data.type === CalcResultDataType.TRAVEL || data.type === CalcResultDataType.TRAVEL_ALLIANZ;

export const isVehicleCalcResultsData = (
  data: TravelCalcResultData | RealtyCalcResultData | VehicleCalcResultData,
): data is VehicleCalcResultData =>
  data.type === CalcResultDataType.MTPL ||
  data.type === CalcResultDataType.MTPL_ALLIANZ ||
  data.type === CalcResultDataType.MTPL_CRASH ||
  data.type === CalcResultDataType.MTPL_CRASH_ALLIANZ ||
  data.type === CalcResultDataType.MTPL_CRASH_KOOP ||
  data.type === CalcResultDataType.PAS ||
  data.type === CalcResultDataType.GAP ||
  data.type === CalcResultDataType.CRASH ||
  data.type === CalcResultDataType.CRASH_KPAS_KOOP;
